import { css, cva } from '@/styled-system/css';
import { styled } from '@/styled-system/jsx';

import { Table } from '@zillow/constellation-10';

import { stickyLeftCellRecipe, tableCellHoverStyles } from '../../../styles/tableFixedScroll';

const TABLE_CELL_BORDER = '1px solid {colors.Table.Cell.border}';

export const PROPERTY_ROW_WIDTHS = {
  FIRST_CELL: '80px',
  MIDDLE_CELL: '50px',
  LAST_CELL: '20px',
} as const;

const IMAGE_DIMENSIONS = {
  WIDTH: 100, // max-width of image
  HEIGHT: 60, // max-height of image
};

export const CELL_DIMENSIONS = {
  WIDTH: 200, // max-width of cell
  HEIGHT: 63, // min-height of cell
};

const hiddenTextStyles = {
  /* bypass the need for text to properly align */
  '&:before': {
    // eslint-disable-next-line curly-quotes/no-straight-quotes
    content: '"hidden"',
    display: 'flex',
    height: '0px',
    fontSize: '0px',
  },
} as const;

export const propertyPhotoContainerStyles = css.raw({
  display: { base: 'none', sm: 'flex' },
  maxWidth: `${IMAGE_DIMENSIONS.WIDTH + 32}px`,
  minHeight: `${IMAGE_DIMENSIONS.HEIGHT}px`,
  width: `104px`,
  outlineWidth: `104px`,
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '600',
  ...hiddenTextStyles,
});

export const propertyPhotoRecipe = cva({
  base: {
    width: `${IMAGE_DIMENSIONS.WIDTH}px`,
    height: `${IMAGE_DIMENSIONS.HEIGHT}px`,
    margin: '0 auto',
    borderRadius: '200',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
  },
  variants: {
    hasPhoto: {
      true: {
        backgroundSize: 'cover',
      },
      false: {
        backgroundSize: 'contain',
      },
    },
  },
});

export const cellAddressContainer = css.raw({
  display: 'flex',
  flexDirection: 'row',
});

// address and summary container parent container
export const flexContainerStyles = css.raw({
  display: 'flex',
  minHeight: `${CELL_DIMENSIONS.HEIGHT}px`,
  alignItems: 'center',
  ...hiddenTextStyles,
});

export const propertyAddressStyles = css.raw({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '100%',
  '& > *': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export const TableHeaderCell = styled(Table.HeaderCell, {
  base: {
    fontSize: 'body-sm',
    fontWeight: 'bold',
    borderTop: TABLE_CELL_BORDER,
    padding: '{spacing.200}',
    display: { base: 'none', lg: 'table-cell' },
    width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,

    '&:first-child': {
      paddingLeft: '{spacing.600}',
      width: PROPERTY_ROW_WIDTHS.FIRST_CELL,
      borderTopLeftRadius: '200',
      display: 'table-cell',
    },

    '&:last-child': {
      width: PROPERTY_ROW_WIDTHS.LAST_CELL,
      borderTopRightRadius: '200',
      display: 'table-cell',
    },

    md: {
      width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,
    },
  },
});

export const TableCell = styled(Table.Cell, {
  base: {
    height: '105px',

    // Target cells in the last row (within the last anchor wrapper)
    'tbody > a:last-child tr > &': {
      borderBottom: 'none',
    },

    '&:first-child': {
      width: 'auto',
      maxWidth: 'none',
    },

    '&:last-child': {
      width: '60px',
      minWidth: '60px',
      maxWidth: '60px',
    },

    md: {
      padding: '{spacing.400} 0 {spacing.400} {spacing.400}',
      '&:last-child': {
        paddingRight: '400',
      },
    },

    lg: {
      paddingLeft: '200',
      width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,
      '&:first-child': {
        paddingLeft: '600',
        width: PROPERTY_ROW_WIDTHS.FIRST_CELL,
      },
      '&:last-child': {
        paddingRight: '200',
        width: PROPERTY_ROW_WIDTHS.LAST_CELL,
      },
    },
  },
});

export const tagStyles = css.raw({
  display: 'flex',
  width: 'fit-content',
  textWrap: 'wrap',
});

export const cellAnchorStyles = css.raw({
  color: 'text.neutral',
  textDecoration: 'none',
  'td:hover &': {
    color: 'blue.500',
    textDecoration: 'underline',
  },
});

// Re-export shared styles for backward compatibility
export { stickyLeftCellRecipe, tableCellHoverStyles };

export const leftDataCellStyles = (isScrolledToLeft: boolean) =>
  css.raw({
    ...stickyLeftCellRecipe.raw({ scrolled: isScrolledToLeft }),
    ...tableCellHoverStyles,
    verticalAlign: 'middle',
    overflow: 'hidden', // Prevent cell content from overflowing

    // Bottom row is always rounded
    'tbody > *:last-child tr:last-of-type &': {
      borderBottom: TABLE_CELL_BORDER,
    },
    // Top row is rounded when header is hidden (mobile only)
    'tbody > *:first-child tr:first-of-type &': {
      lgDown: { borderTopLeftRadius: '200', borderTop: '2px solid {colors.Table.Cell.border}' },
    },
  });

export const rightDataCellStyles = css.raw({
  ...tableCellHoverStyles,

  // Bottom row is always rounded
  'tbody > *:last-child tr:last-of-type &': {
    borderBottom: TABLE_CELL_BORDER,
  },
  // Top row is rounded when header is hidden (mobile only)
  'tbody > *:first-child tr:first-of-type &': {
    lgDown: { borderTopRightRadius: '200', borderTop: '2px solid {colors.Table.Cell.border}' },
  },
});

export const HiddenMobileTableCell = styled(Table.Cell, {
  base: {
    display: { base: 'none', lg: 'table-cell' },
    width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,
    ...tableCellHoverStyles,
  },
});
